import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { runInAction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { Campaign, TAttributionModel, TCampaignGroup } from '@feathr/blackbox';
import { Button, Checkbox, Drawer, Input, Label } from '@feathr/components';
import { useAccount } from '@feathr/extender/state';
import { useReactionEffect } from '@feathr/hooks';
import { AttributionModel } from '@feathr/report_components';

import getColumns, { getColumnIds } from './CampaignsColumns/helpers';

import * as styles from './CampaignsColumnsDrawer.css';

interface IProps {
  attributionModel: TAttributionModel;
  columnIds: string[];
  campaignGroup: TCampaignGroup;
  close: () => void;
  items: Campaign[];
  onChangeSettings: (settings: ISettings) => void;
  isOpen: boolean;
  handleReset?: () => void;
}

interface ISettings {
  attributionModel: TAttributionModel;
  columnIds: string[];
}

const sharedColumns = ['duration', 'date_created', 'date_start', 'date_end', 'views', 'clicks'];

function CampaignsColumnsDrawer({
  campaignGroup,
  close,
  isOpen,
  onChangeSettings,
  items,
  attributionModel,
  columnIds,
  handleReset,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();
  const { eventId: projectId } = useParams<{ eventId?: string }>();
  const alwaysShownColumns = getColumnIds(attributionModel, [
    'name',
    '_cls',
    'state',
    'options',
    'project',
  ]);
  const drawerColumns = useLocalObservable(() => ({
    ids: columnIds,
    search: '',
    setSearch(newSearch: string): void {
      runInAction(() => {
        drawerColumns.search = newSearch;
      });
    },
    attributionModel,
  }));

  function setDrawerColumns(newIds: string[]): void {
    runInAction(() => {
      drawerColumns.ids = newIds;
    });
  }

  function setDrawerAttributionModel(newAttributionModel: TAttributionModel): void {
    runInAction(() => {
      drawerColumns.attributionModel = newAttributionModel;
    });
  }

  useReactionEffect(
    () => columnIds,
    (columnIds) => {
      setDrawerColumns(columnIds);
    },
    {},
    [columnIds],
  );

  useReactionEffect(
    () => attributionModel,
    (attributionModel) => {
      setDrawerAttributionModel(attributionModel);
    },
    {},
    [attributionModel],
  );

  const includeProjectColumn = projectId === undefined;

  const getColumnOptionsMap = (
    attributionModel: TAttributionModel,
  ): Record<TCampaignGroup, string[]> => ({
    ads: ['cpm', 'cpc', 'ctr', 'cpa', ...sharedColumns],
    all: getColumns(attributionModel, includeProjectColumn, account).map((column) => column.id!),
    email: [
      'stats_cache__num_sends_email',
      'stats_cache__email_open_rate',
      'stats_cache__email_click_through_rate',
      'stats_cache__hard_bounce_rate',
      'stats_cache__num_persons_unsubscribed',
      'stats_cache__num_deliveries_email',
      'stats_cache__flavors__pinpoint_tracked_email_hardbounce',
      'stats_cache__num_clicks_active',
      ...sharedColumns,
    ],
    'google-ads': ['project', 'options', ...sharedColumns],
    monetization: [
      'stats_cache__email_open_rate',
      'stats_cache__email_click_through_rate',
      'stats_cache__hard_bounce_rate',
      ...sharedColumns,
    ],
    other: [
      'stats_cache__email_open_rate',
      'stats_cache__email_click_through_rate',
      'stats_cache__hard_bounce_rate',
      ...sharedColumns,
    ],
  });

  const allColumns = getColumns(
    drawerColumns.attributionModel,
    includeProjectColumn,
    account,
  ).filter(({ id }) =>
    getColumnOptionsMap(drawerColumns.attributionModel)[campaignGroup].includes(id!),
  );

  function handleChangeAttributionModel(newAttributionModel: TAttributionModel): void {
    const newColumnIds = columnIds.map((cid) => {
      if (cid.includes('roi')) {
        return `stats_cache__conversions__${newAttributionModel}__roi`;
      } else if (cid.includes('conversions')) {
        return `stats_cache__conversions__${newAttributionModel}__num`;
      }
      return cid;
    });
    setDrawerAttributionModel(newAttributionModel);
    setDrawerColumns(newColumnIds);
  }

  function handleChangeColumn(id: string): void {
    if (drawerColumns.ids.includes(id)) {
      setDrawerColumns(drawerColumns.ids.filter((cid) => cid !== id));
    } else {
      setDrawerColumns([...drawerColumns.ids, id]);
    }
  }

  function handleApplyChanges(): void {
    onChangeSettings({
      attributionModel: drawerColumns.attributionModel,
      columnIds: drawerColumns.ids,
    });
    close();
  }

  function handleSelectAll(): void {
    if (drawerColumns.ids.length - alwaysShownColumns.length === allColumns.length) {
      // Exclude name from being cleared.
      setDrawerColumns(alwaysShownColumns);
    } else {
      setDrawerColumns([...alwaysShownColumns, ...allColumns.map(({ id }) => id!)]);
    }
  }

  function handleClose(): void {
    setDrawerColumns(columnIds);
    setDrawerAttributionModel(attributionModel);
    close();
  }

  function handleSearchChange(newSearch?: string): void {
    drawerColumns.setSearch(newSearch ?? '');
  }

  const filteredColumns = allColumns.filter(({ checkboxLabel }) =>
    checkboxLabel?.toString().toLowerCase()?.includes(drawerColumns.search.toLowerCase()),
  );
  const noResults = filteredColumns.length === 0;

  const children = noResults ? (
    <>{t('No matches found for "{{- search}}"', { search: drawerColumns.search })}</>
  ) : (
    filteredColumns.map(({ id, checkboxLabel }) => {
      function handleChange(): void {
        handleChangeColumn(id!);
      }

      const cannotBeUnchecked = alwaysShownColumns.includes(id!);

      return (
        <Checkbox
          disabled={cannotBeUnchecked}
          id={`checkbox-${id}`}
          key={id}
          label={typeof checkboxLabel === 'string' ? checkboxLabel : checkboxLabel?.(items)}
          name={`checkbox-${id}`}
          onChange={handleChange}
          value={drawerColumns.ids.includes(id!)}
        />
      );
    })
  );
  return (
    <Drawer
      headerSuffix={
        <Input
          isClearable={true}
          onChange={handleSearchChange}
          placeholder={t('Search by name...')}
          prefix={<FontAwesomeIcon icon={faSearch} />}
          type={'text'}
          value={drawerColumns.search}
        />
      }
      isOpen={isOpen}
      name={'customize-columns-drawer'}
      onClose={handleClose}
      title={t('Customize columns')}
    >
      <Drawer.Content addVerticalGap={true}>
        <div className={styles.content}>
          <section>
            <Label>{t('Columns')}</Label>
            <div className={classNames(styles.columns, { [styles.noResults]: noResults })}>
              {children}
            </div>
          </section>
          <section>
            {/* TODO: Only change attribution model if user selects apply. */}
            <AttributionModel
              onChange={handleChangeAttributionModel}
              value={drawerColumns.attributionModel}
            />
          </section>
        </div>
      </Drawer.Content>
      <Drawer.Actions className={classNames({ [styles.actions]: handleReset })}>
        {handleReset && (
          <Button
            key={'reset'}
            name={'reset-to-default'}
            onClick={handleReset}
            tooltip={t('Reset all columns to the default settings')}
          >
            {t('Reset to default')}
          </Button>
        )}
        <Button
          className={styles.rightButtonGroup}
          isFullWidth={!handleReset}
          onClick={handleSelectAll}
        >
          {drawerColumns.ids.length - alwaysShownColumns.length === allColumns.length
            ? t('Unselect all')
            : t('Select all')}
        </Button>
        <Button
          isFullWidth={!handleReset}
          key={'apply'}
          name={'apply'}
          onClick={handleApplyChanges}
          type={'primary'}
        >
          {t('Apply')}
        </Button>
      </Drawer.Actions>
    </Drawer>
  );
}

export default observer(CampaignsColumnsDrawer);
